import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Drawer,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
// NOTE: Tiered pricing is now calculated by the BACKEND using database tiers
// Frontend only displays base prices - discounts are applied server-side

const ShoppingCart = ({ 
  isOpen, 
  onClose, 
  cartItems = [], 
  onUpdateQuantity, 
  onRemoveItem, 
  onCheckout 
}) => {
  // Calculate total quantity for volume discount eligibility hint
  const totalQuantity = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  // Get base price from first cart item
  const basePricePerUnit = useMemo(() => {
    return cartItems.length > 0 && cartItems[0].basePrice ? cartItems[0].basePrice : 1665;
  }, [cartItems]);

  // Check if may qualify for volume discount (just for display hint)
  const mayQualifyForVolumeDiscount = useMemo(() => {
    return totalQuantity >= 20;
  }, [totalQuantity]);

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const itemPrice = item.price || item.basePrice || basePricePerUnit;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const getTotalItems = () => {
    return totalQuantity;
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        '& .MuiDrawer-paper': {
          width: 380,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 1401,
        }
      }}
    >
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '20px',
          py: '18px',
          borderBottom: '1px solid #ececef'
        }}>
          <Typography sx={{
            fontSize: '18px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            color: '#212121'
          }}>
            My Shopping Cart ({getTotalItems()} item{getTotalItems() !== 1 ? 's' : ''})
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#212121',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Cart Items */}
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          px: '20px',
          py: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0px'
        }}>
          {cartItems.length === 0 ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              textAlign: 'center'
            }}>
              <Typography sx={{
                fontSize: '15px',
                fontFamily: 'Poppins, sans-serif',
                color: '#9797a5',
                mb: '4px'
              }}>
                Your cart is empty
              </Typography>
              <Typography sx={{
                fontSize: '13px',
                fontFamily: 'Poppins, sans-serif',
                color: '#9797a5'
              }}>
                Add items to get started
              </Typography>
            </Box>
          ) : (
            cartItems.map((item, index) => (
              <Box key={index} sx={{
                display: 'flex',
                gap: '14px',
                py: '16px',
                borderBottom: index < cartItems.length - 1 ? '1px solid #ececef' : 'none',
                alignItems: 'flex-start'
              }}>
                {/* Left column: image + Remove Item */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <Box sx={{
                    width: '85px',
                    height: '85px',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  <Typography
                    component="span"
                    onClick={() => onRemoveItem(index)}
                    sx={{
                      fontSize: '12px',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#212121',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      lineHeight: '16px',
                      '&:hover': { color: '#e53e3e' }
                    }}
                  >
                    Remove item
                  </Typography>
                </Box>

                {/* Product Details */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {/* Product name */}
                  <Typography sx={{
                    fontSize: '15px',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    color: '#212121',
                    lineHeight: '21px'
                  }}>
                    {item.name}
                  </Typography>

                  {/* Volume pill — shown if item has a volume label */}
                  {item.volume && (
                    <Box sx={{
                      display: 'inline-flex',
                      alignSelf: 'flex-start',
                      px: '10px',
                      py: '2px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '20px',
                      border: '1px solid #d8d8d8'
                    }}>
                      <Typography sx={{
                        fontSize: '11px',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 500,
                        color: '#444444',
                        lineHeight: '16px'
                      }}>
                        {item.volume}
                      </Typography>
                    </Box>
                  )}

                  {/* Price per vial */}
                  <Typography sx={{
                    fontSize: '13px',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    color: '#212121',
                    lineHeight: '18px'
                  }}>
                    {item.discountPercent > 0 ? (
                      <>
                        <Box component="span" sx={{ textDecoration: 'line-through', color: '#999', fontWeight: 400, mr: '6px' }}>
                          ${(item.basePrice || basePricePerUnit).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                        </Box>
                        ${(item.price || item.basePrice || basePricePerUnit).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                        <Box component="span" sx={{ fontWeight: 400 }}>/Vial</Box>
                      </>
                    ) : (
                      <>
                        ${(item.basePrice || basePricePerUnit).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                        <Box component="span" sx={{ fontWeight: 400 }}>/Vial</Box>
                      </>
                    )}
                  </Typography>

                  {/* Discount badge */}
                  {item.discountPercent > 0 && (
                    <Box sx={{
                      display: 'inline-flex',
                      alignSelf: 'flex-start',
                      px: '8px',
                      py: '2px',
                      backgroundColor: '#ecfdf5',
                      borderRadius: '20px',
                      border: '1px solid #a7f3d0'
                    }}>
                      <Typography sx={{ fontSize: '11px', fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: '#059669', lineHeight: '16px' }}>
                        {item.discountPercent}% Volume Discount
                      </Typography>
                    </Box>
                  )}

                  {/* Qty × price */}
                  <Typography sx={{
                    fontSize: '12px',
                    fontFamily: 'Poppins, sans-serif',
                    color: '#666666',
                    lineHeight: '17px'
                  }}>
                    ${(item.price || item.basePrice || basePricePerUnit).toLocaleString('en-US', { minimumFractionDigits: 0 })} x {item.quantity}
                  </Typography>

                  {/* Quantity Stepper — pill style */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'flex-end',
                    mt: '4px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '999px',
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0'
                  }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newQuantity = (item.quantity || 1) - 1;
                        onUpdateQuantity(index, Math.max(0, newQuantity));
                      }}
                      disabled={!item.quantity || item.quantity <= 0}
                      sx={{
                        color: (!item.quantity || item.quantity <= 0) ? '#ccc' : '#212121',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%'
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>

                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          onUpdateQuantity(index, '');
                        } else {
                          const numValue = parseInt(value, 10);
                          if (!isNaN(numValue)) {
                            onUpdateQuantity(index, numValue);
                          }
                        }
                      }}
                      inputProps={{
                        style: {
                          textAlign: 'center',
                          padding: '0',
                          fontSize: '14px',
                          fontWeight: 600,
                          fontFamily: 'Poppins, sans-serif',
                          width: '28px',
                          MozAppearance: 'textfield'
                        }
                      }}
                      sx={{
                        width: '28px',
                        '& .MuiOutlinedInput-root': {
                          height: '32px',
                          backgroundColor: 'transparent',
                          '& fieldset': { border: 'none' },
                          '&:hover fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none' },
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                            '&::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            '&::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                          }
                        }
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                      sx={{
                        color: '#212121',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%'
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Footer */}
        {cartItems.length > 0 && (
          <Box sx={{
            borderTop: '1px solid #ececef',
            px: '20px',
            py: '20px',
            backgroundColor: '#ffffff'
          }}>
            {/* Subtotal */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: '16px'
            }}>
              <Typography sx={{
                fontSize: '15px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                color: '#212121'
              }}>
                Subtotal
              </Typography>
              <Typography sx={{
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                color: '#212121'
              }}>
                ${calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={onCheckout}
                sx={{
                  backgroundColor: '#039582',
                  color: 'white',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '15px',
                  height: '48px',
                  borderRadius: '24px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  border: 'none',
                  '&:hover': { backgroundColor: '#027a6b', boxShadow: 'none' }
                }}
              >
                Go to Checkout
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={onClose}
                sx={{
                  backgroundColor: 'rgba(86, 172, 89, 0.10)',
                  color: '#039582',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '15px',
                  height: '48px',
                  borderRadius: '24px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  border: 'none',
                  '&:hover': { backgroundColor: 'rgba(86, 172, 89, 0.18)', boxShadow: 'none' }
                }}
              >
                Continue Shopping
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ShoppingCart;
